import { createContext, useContext, useState, useCallback, useEffect } from "react";

const LANGS = [
  { code: "en", label: "EN" },
  { code: "es", label: "ES" },
  { code: "zh", label: "中文" },
];

const T = {
  en: {
    nav: { species: "Species", heritage: "Heritage", markets: "Markets", reach: "Global Reach", brochure: "Brochure", quote: "Request a Quote", downloadBrochure: "Download Brochure" },
    hero: {
      kicker: "Family Owned Lumber Exporters · Est. 2007",
      lines: ["U.S. & CANADIAN", "TIMBER, EXPORTED", "TO THE WORLD"],
      sub: "7 Seas Group USA supplies premium North American hardwoods and softwoods to mills, importers and manufacturers across six continents — graded, kiln-dried and shipped with precision.",
      explore: "Explore Species",
    },
    marquee: ["PREMIUM EXPORT GRADE", "FSC® CERTIFIED", "FAMILY OWNED SINCE 2007", "KILN-DRIED & GRADED", "GLOBAL REACH", "U.S. & CANADIAN SPECIES"],
    catalog: {
      kicker: "01 — The Catalog",
      title: "A curated inventory of North American species.",
      sub: "From Appalachian White Oak to British Columbian Cedar — every board is selected, graded and documented for international trade.",
      Hardwoods: "Hardwoods",
      Softwoods: "Softwoods",
    },
    compare: {
      kicker: "02 — Compare Species",
      title: "Pick the right wood, side by side.",
      sub: "Select up to four species to compare density, hardness, workability and principal uses at a glance.",
      pick: "Select up to 4 species",
      type: "Type", density: "Density", hardness: "Hardness (Janka)", workability: "Workability", uses: "Principal Uses",
    },
    grades: {
      kicker: "03 — The Grade Guide",
      title: "NHLA grades, explained.",
      sub: "Every hardwood board we ship is graded to National Hardwood Lumber Association rules — the yield of clear, defect-free cuttings determines the grade.",
      clear: "Clear-face yield",
      minBoard: "Min. board size",
      bestFor: "Best for",
      note: "Grades are set by the percentage of clear, usable cuttings on a board's poorer face — not by looks alone. Our export team helps you match grade to end use, often cutting cost without compromising the finished product.",
      items: [
        { name: "FAS", full: "Firsts & Seconds", size: "6\" × 8'", desc: "The premium grade. Long, wide, mostly clear cuttings on both faces of the board — the benchmark for high-end work and the most requested export specification.", for: "Fine furniture, long mouldings, architectural millwork" },
        { name: "F1F / Selects", full: "FAS One Face", size: "4\" × 6'", desc: "An FAS face on the front with a No.1 Common reverse. Delivers premium show-face quality at better value when only one face will be visible.", for: "Cabinet fronts, panelling, one-face applications" },
        { name: "No.1 Common", full: "The Cabinet Grade", size: "3\" × 4'", desc: "Shorter and narrower clear cuttings at a significantly lower price. The workhorse specification for parts that are cut down anyway.", for: "Kitchen cabinets, furniture parts, flooring" },
        { name: "No.2A Common", full: "The Economy Grade", size: "3\" × 4'", desc: "Half the board yields clear cuttings — ideal where smaller components or natural character are wanted and budget matters.", for: "Flooring, smaller components, rustic character work" },
      ],
    },
    manifesto: {
      kicker: "04 — The Manifesto",
      title: "Built on grain, grit and generations.",
      chapters: [
        { title: "The Heritage", body: "Founded in 2007 as a family enterprise, 7 Seas Group USA grew from a single yard into a trusted name in international lumber trade. Two generations, one standard." },
        { title: "The Sourcing", body: "We partner directly with responsibly managed forests across the United States and Canada — securing consistent supply of hardwood and softwood species at export grade." },
        { title: "The Process", body: "Every shipment is graded to NHLA rules, kiln-dried to spec, and documented for phytosanitary and customs compliance before it leaves the port." },
        { title: "The Promise", body: "Transparent pricing, reliable logistics and lumber you can trust — order after order, container after container, continent after continent." },
      ],
    },
    reach: {
      kicker: "05 — Global Reach",
      title: "From North American forests to ports worldwide.",
      stats: ["Countries served", "Family owned since", "Species exported", "Continents reached"],
      certs: [
        "Forest Stewardship Council chain-of-custody sourcing.",
        "Programme for the Endorsement of Forest Certification.",
        "Graded to National Hardwood Lumber Association rules.",
      ],
    },
    map: {
      kicker: "06 — Global Markets",
      title: "Shipped from North America to the world's ports.",
      sub: "Hover a market to trace the route from our U.S. yards to the ports we serve across six continents.",
    },
    calc: {
      kicker: "07 — Load Calculator",
      title: "How much lumber fits your container?",
      sub: "Choose a container and species to estimate the board footage per shipment — volume and payload limits included.",
      container: "Container", species: "Species",
      boardFeet: "Est. Board Feet", netVolume: "Net Volume",
      limit: "Limiting factor", byWeight: "Weight", byVolume: "Volume",
      note: "Estimates assume kiln-dried lumber, ≈85% stowage efficiency and standard payload limits. Actual loads vary by pack sizes, moisture and road limits — our team confirms exact figures with your quote.",
      cta: "Request a quote for this load",
    },
    quote: {
      kicker: "08 — Request a Quote",
      title: "Let's talk volume, species and destination.",
      sub: "Tell us what you need and where it's headed. Our team responds to every serious inquiry with pricing and availability.",
      name: "Full Name *", email: "Email *", company: "Company", country: "Destination Country",
      species: "Species of Interest", volume: "Estimated Volume", message: "Message *",
      phName: "Jane Doe", phEmail: "jane@company.com", phCompany: "Company Ltd.", phCountry: "Germany",
      phSpecies: "White Oak, Walnut...", phVolume: "2 x 40ft containers", phMessage: "Tell us about your requirements...",
      submit: "Submit Inquiry",
      errFill: "Please fill in your name, email and message.",
      opening: "Opening your email app with your inquiry details...",
    },
    footer: {
      blurb: "A family-owned lumber export company supplying premium U.S. and Canadian hardwoods and softwoods to the world since 2007.",
      navigate: "Navigate", contact: "Contact", email: "Email", phone: "Phone",
      hq: "Headquarters", hqValue: "United States of America",
      brochure: "Capabilities Brochure", rights: "All rights reserved", family: "Family Owned · Est. 2007",
    },
    detail: {
      overview: "Overview", grades: "Available Grades", dims: "Dimensions", uses: "Principal Uses",
      chars: "Characteristics", colour: "Colour", density: "Density", hardness: "Hardness", workability: "Workability",
      quoteBtn: "Request a Quote", allSpecies: "All Species", gallery: "In Grain & In Use",
      zoomHint: "Click image to zoom",
      more: "More {type} species", typeHardwood: "hardwood", typeSoftwood: "softwood",
      notFound: "Species not found", back: "Back to catalog",
    },
    whatsapp: "Chat with our export team",
  },
  es: {
    nav: { species: "Especies", heritage: "Legado", markets: "Mercados", reach: "Alcance Global", brochure: "Folleto", quote: "Solicitar Cotización", downloadBrochure: "Descargar Folleto" },
    hero: {
      kicker: "Exportadores de Madera Familiares · Desde 2007",
      lines: ["MADERA DE EE. UU.", "Y CANADÁ, EXPORTADA", "AL MUNDO"],
      sub: "7 Seas Group USA suministra maderas duras y blandas norteamericanas de primera calidad a aserraderos, importadores y fabricantes en seis continentes — clasificadas, secadas en horno y enviadas con precisión.",
      explore: "Explorar Especies",
    },
    marquee: ["CALIDAD PREMIUM DE EXPORTACIÓN", "CERTIFICADO FSC®", "EMPRESA FAMILIAR DESDE 2007", "SECADO EN HORNO Y CLASIFICADO", "ALCANCE GLOBAL", "ESPECIES DE EE. UU. Y CANADÁ"],
    catalog: {
      kicker: "01 — El Catálogo",
      title: "Un inventario curado de especies norteamericanas.",
      sub: "Del Roble Blanco de los Apalaches al Cedro de Columbia Británica: cada tabla se selecciona, clasifica y documenta para el comercio internacional.",
      Hardwoods: "Maderas Duras",
      Softwoods: "Maderas Blandas",
    },
    compare: {
      kicker: "02 — Comparar Especies",
      title: "Elija la madera correcta, lado a lado.",
      sub: "Seleccione hasta cuatro especies para comparar densidad, dureza, trabajabilidad y usos principales de un vistazo.",
      pick: "Seleccione hasta 4 especies",
      type: "Tipo", density: "Densidad", hardness: "Dureza (Janka)", workability: "Trabajabilidad", uses: "Usos Principales",
    },
    grades: {
      kicker: "03 — Guía de Calidades",
      title: "Las calidades NHLA, explicadas.",
      sub: "Cada tabla de madera dura que enviamos se clasifica según las reglas de la National Hardwood Lumber Association: el rendimiento de cortes limpios y sin defectos determina la calidad.",
      clear: "Rendimiento de cara limpia",
      minBoard: "Tamaño mín. de tabla",
      bestFor: "Ideal para",
      note: "Las calidades se definen por el porcentaje de cortes limpios y aprovechables en la peor cara de la tabla — no solo por la apariencia. Nuestro equipo le ayuda a elegir la calidad según el uso final, a menudo reduciendo costos sin comprometer el producto terminado.",
      items: [
        { name: "FAS", full: "Firsts & Seconds", size: "6\" × 8'", desc: "La calidad premium. Cortes largos, anchos y mayormente limpios en ambas caras — la referencia para trabajos de alta gama y la especificación de exportación más solicitada.", for: "Muebles finos, molduras largas, carpintería arquitectónica" },
        { name: "F1F / Selects", full: "FAS Una Cara", size: "4\" × 6'", desc: "Cara FAS al frente con reverso No.1 Common. Ofrece calidad premium en la cara vista con mejor valor cuando solo una cara será visible.", for: "Frentes de gabinetes, paneles, aplicaciones de una cara" },
        { name: "No.1 Common", full: "La Calidad de Ebanistería", size: "3\" × 4'", desc: "Cortes limpios más cortos y estrechos a un precio significativamente menor. La especificación de batalla para piezas que de todos modos se recortan.", for: "Gabinetes de cocina, piezas de muebles, pisos" },
        { name: "No.2A Common", full: "La Calidad Económica", size: "3\" × 4'", desc: "La mitad de la tabla rinde cortes limpios — ideal donde se requieren componentes pequeños o carácter natural y el presupuesto importa.", for: "Pisos, componentes pequeños, trabajos rústicos" },
      ],
    },
    manifesto: {
      kicker: "04 — El Manifiesto",
      title: "Construido sobre veta, esfuerzo y generaciones.",
      chapters: [
        { title: "El Legado", body: "Fundada en 2007 como empresa familiar, 7 Seas Group USA creció de un solo almacén a un nombre de confianza en el comercio internacional de madera. Dos generaciones, un mismo estándar." },
        { title: "El Abastecimiento", body: "Colaboramos directamente con bosques gestionados responsablemente en Estados Unidos y Canadá, asegurando un suministro constante de especies de madera dura y blanda en calidad de exportación." },
        { title: "El Proceso", body: "Cada envío se clasifica según las reglas NHLA, se seca en horno según especificación y se documenta para el cumplimiento fitosanitario y aduanero antes de salir del puerto." },
        { title: "La Promesa", body: "Precios transparentes, logística confiable y madera en la que puede confiar — pedido tras pedido, contenedor tras contenedor, continente tras continente." },
      ],
    },
    reach: {
      kicker: "05 — Alcance Global",
      title: "De los bosques norteamericanos a puertos de todo el mundo.",
      stats: ["Países atendidos", "Empresa familiar desde", "Especies exportadas", "Continentes alcanzados"],
      certs: [
        "Abastecimiento con cadena de custodia del Forest Stewardship Council.",
        "Programa para el Reconocimiento de Certificación Forestal.",
        "Clasificado según las reglas de la National Hardwood Lumber Association.",
      ],
    },
    map: {
      kicker: "06 — Mercados Globales",
      title: "Enviado desde Norteamérica a los puertos del mundo.",
      sub: "Pase el cursor sobre un mercado para trazar la ruta desde nuestros almacenes en EE. UU. hasta los puertos que servimos en seis continentes.",
    },
    calc: {
      kicker: "07 — Calculadora de Carga",
      title: "¿Cuánta madera cabe en su contenedor?",
      sub: "Elija un contenedor y una especie para estimar los pies tablares por envío — con límites de volumen y peso incluidos.",
      container: "Contenedor", species: "Especie",
      boardFeet: "Pies Tablares Est.", netVolume: "Volumen Neto",
      limit: "Factor limitante", byWeight: "Peso", byVolume: "Volumen",
      note: "Las estimaciones asumen madera secada en horno, ≈85% de eficiencia de estiba y límites de carga estándar. Las cargas reales varían según paquetes, humedad y límites viales — nuestro equipo confirma cifras exactas con su cotización.",
      cta: "Solicitar cotización para esta carga",
    },
    quote: {
      kicker: "08 — Solicitar Cotización",
      title: "Hablemos de volumen, especies y destino.",
      sub: "Cuéntenos qué necesita y hacia dónde va. Nuestro equipo responde a cada consulta seria con precios y disponibilidad.",
      name: "Nombre Completo *", email: "Correo Electrónico *", company: "Empresa", country: "País de Destino",
      species: "Especies de Interés", volume: "Volumen Estimado", message: "Mensaje *",
      phName: "Juana Pérez", phEmail: "juana@empresa.com", phCompany: "Empresa S.A.", phCountry: "España",
      phSpecies: "Roble Blanco, Nogal...", phVolume: "2 contenedores de 40 pies", phMessage: "Cuéntenos sus requerimientos...",
      submit: "Enviar Consulta",
      errFill: "Por favor complete su nombre, correo y mensaje.",
      opening: "Abriendo su aplicación de correo con los detalles de su consulta...",
    },
    footer: {
      blurb: "Una empresa familiar exportadora de madera que suministra maderas duras y blandas de EE. UU. y Canadá de primera calidad al mundo desde 2007.",
      navigate: "Navegar", contact: "Contacto", email: "Correo", phone: "Teléfono",
      hq: "Sede", hqValue: "Estados Unidos de América",
      brochure: "Folleto de Capacidades", rights: "Todos los derechos reservados", family: "Empresa Familiar · Desde 2007",
    },
    detail: {
      overview: "Descripción", grades: "Calidades Disponibles", dims: "Dimensiones", uses: "Usos Principales",
      chars: "Características", colour: "Color", density: "Densidad", hardness: "Dureza", workability: "Trabajabilidad",
      quoteBtn: "Solicitar Cotización", allSpecies: "Todas las Especies", gallery: "Veta y Aplicaciones",
      zoomHint: "Haga clic para ampliar",
      more: "Más especies de {type}", typeHardwood: "madera dura", typeSoftwood: "madera blanda",
      notFound: "Especie no encontrada", back: "Volver al catálogo",
    },
    whatsapp: "Chatee con nuestro equipo de exportación",
  },
  zh: {
    nav: { species: "树种", heritage: "传承", markets: "市场", reach: "全球网络", brochure: "宣传册", quote: "索取报价", downloadBrochure: "下载宣传册" },
    hero: {
      kicker: "家族经营的木材出口商 · 始于2007年",
      lines: ["美国与加拿大木材", "出口至", "世界各地"],
      sub: "7 Seas Group USA 向六大洲的工厂、进口商和制造商供应优质北美硬木和软木——精心分级、窑干并精准发运。",
      explore: "浏览树种",
    },
    marquee: ["优质出口等级", "FSC® 认证", "家族经营 · 始于2007", "窑干与分级", "全球网络", "美国与加拿大树种"],
    catalog: {
      kicker: "01 — 产品目录",
      title: "精选北美树种库存。",
      sub: "从阿巴拉契亚白橡木到不列颠哥伦比亚雪松——每块板材均经过挑选、分级并附有国际贸易文件。",
      Hardwoods: "硬木",
      Softwoods: "软木",
    },
    compare: {
      kicker: "02 — 树种对比",
      title: "并排对比，快速选对木材。",
      sub: "最多选择四个树种，一目了然地对比密度、硬度、加工性和主要用途。",
      pick: "最多选择4个树种",
      type: "类型", density: "密度", hardness: "硬度（Janka）", workability: "加工性", uses: "主要用途",
    },
    grades: {
      kicker: "03 — 等级指南",
      title: "NHLA 等级详解。",
      sub: "我们出口的每一块硬木板材都按美国硬木板材协会（NHLA）规则分级——无缺陷净划面出材率决定等级。",
      clear: "净划面出材率",
      minBoard: "最小板材尺寸",
      bestFor: "最适用于",
      note: "等级由板材较差一面的净划面可用出材率决定，而非仅凭外观。我们的出口团队会帮您按最终用途匹配等级，往往能在不影响成品质量的前提下节省成本。",
      items: [
        { name: "FAS", full: "一等与二等", size: "6\" × 8'", desc: "最高等级。板材两面均可获得长、宽、基本无缺陷的净划面——高端产品的标杆，也是最常见的出口规格。", for: "高级家具、长线条、建筑木制品" },
        { name: "F1F / Selects", full: "单面FAS", size: "4\" × 6'", desc: "正面为FAS级，背面为一号普通级。当只有一面外露时，以更优价格获得正面的顶级品质。", for: "柜门面板、护墙板、单面应用" },
        { name: "No.1 Common", full: "橱柜级", size: "3\" × 4'", desc: "净划面较短较窄，但价格显著更低。对于本就需要裁切的部件而言是主力规格。", for: "厨柜、家具部件、地板" },
        { name: "No.2A Common", full: "经济级", size: "3\" × 4'", desc: "板材一半可出净划面——适合需要小部件或自然木材特征、且注重预算的场合。", for: "地板、小部件、乡村风格制品" },
      ],
    },
    manifesto: {
      kicker: "04 — 企业宣言",
      title: "建立在木纹、毅力与世代传承之上。",
      chapters: [
        { title: "传承", body: "7 Seas Group USA 成立于2007年，是一家家族企业，从一个木材场发展成为国际木材贸易的可信赖品牌。两代人，同一标准。" },
        { title: "采购", body: "我们与美国和加拿大负责任管理的森林直接合作，确保以出口等级稳定供应硬木和软木树种。" },
        { title: "工艺", body: "每批货物均按 NHLA 规则分级、按规格窑干，并在离港前完成植物检疫和海关合规文件。" },
        { title: "承诺", body: "透明的价格、可靠的物流和值得信赖的木材——一单接一单，一柜接一柜，一洲接一洲。" },
      ],
    },
    reach: {
      kicker: "05 — 全球网络",
      title: "从北美森林到世界各地的港口。",
      stats: ["服务国家", "家族经营始于", "出口树种", "覆盖大洲"],
      certs: ["森林管理委员会产销监管链采购。", "森林认证认可计划。", "按美国硬木板材协会（NHLA）规则分级。"],
    },
    map: {
      kicker: "06 — 全球市场",
      title: "从北美发运至世界各大港口。",
      sub: "将鼠标悬停在市场上，即可查看从我们美国货场到六大洲港口的航线。",
    },
    calc: {
      kicker: "07 — 装柜计算器",
      title: "您的集装箱能装多少木材？",
      sub: "选择集装箱和树种，即可估算每票货的板英尺数——已包含容积和载重限制。",
      container: "集装箱", species: "树种",
      boardFeet: "预计板英尺", netVolume: "净材积",
      limit: "限制因素", byWeight: "载重", byVolume: "容积",
      note: "估算基于窑干木材、约85%的装载效率和标准载重限制。实际装载量因打包规格、含水率和公路限重而异——我们的团队将在报价时确认准确数字。",
      cta: "为此货载索取报价",
    },
    quote: {
      kicker: "08 — 索取报价",
      title: "让我们谈谈数量、树种和目的地。",
      sub: "告诉我们您的需求和目的地。我们的团队会以价格和库存信息回复每一个认真的询盘。",
      name: "姓名 *", email: "电子邮箱 *", company: "公司", country: "目的地国家",
      species: "感兴趣的树种", volume: "预计数量", message: "留言 *",
      phName: "张伟", phEmail: "zhang@company.com", phCompany: "公司名称", phCountry: "中国",
      phSpecies: "白橡木、黑胡桃…", phVolume: "2个40英尺集装箱", phMessage: "请告诉我们您的需求…",
      submit: "提交询盘",
      errFill: "请填写您的姓名、电子邮箱和留言。",
      opening: "正在打开您的邮件应用并填入询盘信息…",
    },
    footer: {
      blurb: "一家家族经营的木材出口公司，自2007年以来向全球供应优质的美国和加拿大硬木与软木。",
      navigate: "导航", contact: "联系方式", email: "电子邮箱", phone: "电话",
      hq: "总部", hqValue: "美利坚合众国",
      brochure: "能力宣传册", rights: "版权所有", family: "家族经营 · 始于2007",
    },
    detail: {
      overview: "概述", grades: "可供等级", dims: "规格尺寸", uses: "主要用途",
      chars: "材性特征", colour: "颜色", density: "密度", hardness: "硬度", workability: "加工性",
      quoteBtn: "索取报价", allSpecies: "全部树种", gallery: "木纹与应用",
      zoomHint: "点击图片放大",
      more: "更多{type}树种", typeHardwood: "硬木", typeSoftwood: "软木",
      notFound: "未找到该树种", back: "返回目录",
    },
    whatsapp: "与我们的出口团队洽谈",
  },
};

const LangContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => localStorage.getItem("sevenseas_lang") || "en");
  const setLang = useCallback((l) => {
    localStorage.setItem("sevenseas_lang", l);
    setLangState(l);
    document.documentElement.lang = l;
  }, []);
  const t = useCallback(
    (key) => {
      const walk = (dict) => key.split(".").reduce((o, k) => (o == null ? o : o[k]), dict);
      const v = walk(T[lang]);
      return v != null ? v : walk(T.en);
    },
    [lang]
  );
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);
  return <LangContext.Provider value={{ lang, setLang, t, langs: LANGS }}>{children}</LangContext.Provider>;
};

export const useLang = () => useContext(LangContext);
